/**
 * Subscription Utilities
 * Helper functions for checking subscription features and limits
 */

import {
  Subscription,
  SubscriptionFeature,
} from '../services/subscriptionService';
import {
  SUBSCRIPTION_FEATURES,
  SUBSCRIPTION_TIERS,
} from '../constants/subscriptionFeatures';

/**
 * Check if a specific feature is active in the subscription
 * @param subscription - The subscription object (can be null)
 * @param featureName - The name of the feature to check
 * @returns true if feature exists and is_active is true
 */
export const hasSubscriptionFeature = (
  subscription: Subscription | null | undefined,
  featureName: string
): boolean => {
  if (!subscription?.features) return false;

  const feature = subscription.features.find(
    f => f.feature_name === featureName
  );
  return feature?.is_active ?? false;
};

/**
 * Get a specific feature object by name
 * @param subscription - The subscription object
 * @param featureName - The name of the feature to find
 * @returns The feature object or null if not found
 */
export const getFeatureByName = (
  subscription: Subscription | null | undefined,
  featureName: string
): SubscriptionFeature | null => {
  if (!subscription?.features) return null;

  return (
    subscription.features.find(f => f.feature_name === featureName) || null
  );
};

/**
 * Get the numeric limit from a feature's additional_info field
 * @param subscription - The subscription object
 * @param featureName - The name of the feature
 * @returns The numeric limit, or 0 if not found or not a number
 */
export const getFeatureLimit = (
  subscription: Subscription | null | undefined,
  featureName: string
): number => {
  const feature = getFeatureByName(subscription, featureName);

  if (!feature?.additional_info) return 0;

  const limit = parseInt(feature.additional_info, 10);
  return isNaN(limit) ? 0 : limit;
};

/**
 * Get the maximum upload limit for a specific type of upload
 * Handles pattern matching for upload features (e.g., "Upload X photos to...")
 * @param subscription - The subscription object
 * @param uploadType - Type of upload (e.g., 'Hotel Gallery', 'Sustainability Gallery')
 * @returns The maximum upload count
 */
export const getUploadLimit = (
  subscription: Subscription | null | undefined,
  uploadType: 'Hotel Gallery' | 'Sustainability Gallery' | 'Video'
): number => {
  if (!subscription?.features) return 0;

  // Find all features matching the upload type
  const uploadFeatures = subscription.features.filter(
    f =>
      f.feature_name.includes(`Upload`) &&
      f.feature_name.includes(uploadType) &&
      f.is_active
  );

  if (uploadFeatures.length === 0) return 0;

  // Find the highest limit among active features
  const limits = uploadFeatures
    .map(f => parseInt(f.additional_info || '0', 10))
    .filter(limit => !isNaN(limit));

  return limits.length > 0 ? Math.max(...limits) : 0;
};

/**
 * Check if more uploads are allowed based on current count and subscription limit
 * @param subscription - The subscription object
 * @param uploadType - Type of upload
 * @param currentCount - Current number of uploaded items
 * @returns true if more uploads are allowed
 */
export const canUploadMore = (
  subscription: Subscription | null | undefined,
  uploadType: 'Hotel Gallery' | 'Sustainability Gallery' | 'Video',
  currentCount: number
): boolean => {
  const limit = getUploadLimit(subscription, uploadType);
  return currentCount < limit;
};

/**
 * Check if a feature is active by searching in the features array
 * @param features - Array of subscription features
 * @param featureName - The name of the feature to check
 * @returns true if feature is active
 */
export const isFeatureActive = (
  features: SubscriptionFeature[] | undefined,
  featureName: string
): boolean => {
  if (!features) return false;

  const feature = features.find(f => f.feature_name === featureName);
  return feature?.is_active ?? false;
};

/**
 * Check if the subscription is a specific tier
 * @param subscription - The subscription object
 * @param tier - The tier to check against
 * @returns true if subscription matches the tier
 */
export const isSubscriptionTier = (
  subscription: Subscription | null | undefined,
  tier: keyof typeof SUBSCRIPTION_TIERS
): boolean => {
  if (!subscription) return false;
  return subscription.subscription_code === SUBSCRIPTION_TIERS[tier];
};

/**
 * Check if the subscription is FREE tier
 * @param subscription - The subscription object
 * @returns true if subscription is FREE tier
 */
export const isFreeSubscription = (
  subscription: Subscription | null | undefined
): boolean => {
  return isSubscriptionTier(subscription, 'FREE');
};

export const isEssentialsSubscription = (
  subscription: Subscription | null | undefined
): boolean => {
  return isSubscriptionTier(subscription, 'ESSENTIALS');
};

/**
 * Check if the subscription is BOOST tier
 * @param subscription - The subscription object
 * @returns true if subscription is BOOST tier
 */
export const isBoostSubscription = (
  subscription: Subscription | null | undefined
): boolean => {
  return isSubscriptionTier(subscription, 'BOOST');
};

/**
 * Get all active features from a subscription
 * @param subscription - The subscription object
 * @returns Array of active feature names
 */
export const getActiveFeatures = (
  subscription: Subscription | null | undefined
): string[] => {
  if (!subscription?.features) return [];

  return subscription.features
    .filter(f => f.is_active)
    .map(f => f.feature_name);
};

/**
 * Get subscription tier display name
 * @param subscription - The subscription object
 * @returns Formatted tier name or 'Unknown'
 */
export const getSubscriptionTierName = (
  subscription: Subscription | null | undefined
): string => {
  if (!subscription) return 'Unknown';
  return (
    subscription.subscription_name ||
    subscription.subscription_code ||
    'Unknown'
  );
};

/**
 * Check if user can view detailed scores (pillar scores)
 * @param subscription - The subscription object
 * @returns true if user can view pillar scores
 */
export const canViewPillarScores = (
  subscription: Subscription | null | undefined
): boolean => {
  return hasSubscriptionFeature(
    subscription,
    SUBSCRIPTION_FEATURES.VIEW_PILLAR_SCORES
  );
};

/**
 * Check if user can view category scores (detailed breakdown)
 * @param subscription - The subscription object
 * @returns true if user can view category scores
 */
export const canViewCategoryScores = (
  subscription: Subscription | null | undefined
): boolean => {
  return hasSubscriptionFeature(
    subscription,
    SUBSCRIPTION_FEATURES.VIEW_CATEGORY_SCORES
  );
};

/**
 * Check if user can upload videos
 * @param subscription - The subscription object
 * @returns true if video upload is available
 */
export const canUploadVideo = (
  subscription: Subscription | null | undefined
): boolean => {
  return hasSubscriptionFeature(
    subscription,
    SUBSCRIPTION_FEATURES.VIDEO_UPLOAD_1
  );
};

/**
 * Check if user can write mission statement
 * @param subscription - The subscription object
 * @returns true if mission statement feature is active
 */
export const canWriteMissionStatement = (
  subscription: Subscription | null | undefined
): boolean => {
  return hasSubscriptionFeature(
    subscription,
    SUBSCRIPTION_FEATURES.MISSION_STATEMENT
  );
};

export const canUploadGBTA = (
  subscription: Subscription | null | undefined
): boolean => {
  // Currently available to all subscriptions
  return hasSubscriptionFeature(
    subscription,
    SUBSCRIPTION_FEATURES.UPLOAD_GBTA_STANDARDS
  );
};


export const canShowUpgradeBanner = (
  subscription: Subscription | null | undefined
): boolean => {
  return isFreeSubscription(subscription) || isEssentialsSubscription(subscription);
};